import { useState, useCallback, useRef } from "react";
import { countFilteredPrompts, countTotalPrompts } from "@/utils/exploreQueries";

interface UseCategoryFilterProps {
  initialCategoryId?: string | null;
  onCategoryChange?: (categoryId: string | null) => void;
}

export function useCategoryFilter({ 
  initialCategoryId = null,
  onCategoryChange 
}: UseCategoryFilterProps = {}) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(initialCategoryId);
  const [totalItems, setTotalItems] = useState<number | undefined>(undefined);
  const isLoadingCount = useRef(false);

  const handleCategoryFilter = useCallback(async (categoryId: string) => {
    console.log(`Category filter requested: ${categoryId}`);
    
    // Handle unselect - empty string means unselect
    const newCategoryId = categoryId === '' ? null : categoryId;
    
    // Don't do anything if the category is already selected
    if (selectedCategory === newCategoryId) {
      console.log("Category already selected, no change needed");
      return;
    }
    
    setSelectedCategory(newCategoryId);
    
    // Notify parent component of category change
    if (onCategoryChange) {
      onCategoryChange(newCategoryId);
    }
    
    // Skip count loading if already in progress
    if (isLoadingCount.current) {
      console.log("Count loading already in progress, skipping");
      return;
    }
    
    isLoadingCount.current = true;
    
    try {
      let count: number;
      if (newCategoryId) {
        console.log(`Loading count for category: ${newCategoryId}`);
        count = await countFilteredPrompts(newCategoryId);
      } else {
        console.log("Loading total count for all prompts");
        count = await countTotalPrompts();
      }
      
      console.log(`Category count loaded: ${count}`);
      setTotalItems(count);
    } catch (error) {
      console.error("Error loading category count:", error);
      // Don't update count on error, keep previous value
    } finally {
      isLoadingCount.current = false;
    }
  }, [selectedCategory, onCategoryChange]);

  const clearCategoryFilter = useCallback(() => {
    console.log("Clearing category filter");
    handleCategoryFilter(''); // Use empty string to trigger unselect
  }, [handleCategoryFilter]);

  return {
    selectedCategory,
    handleCategoryFilter,
    clearCategoryFilter,
    setSelectedCategory,
    totalItems
  };
}
